import { useParams, Link } from 'react-router-dom';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import { articles } from '@/mocks/articles';
import JsonLd from '@/components/base/JsonLd';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://pandabuy.asia';

function buildArticleSchema(article: typeof articles[0]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    author: {
      '@type': 'Organization',
      name: 'Pandabuy.asia',
      url: siteUrl,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Pandabuy.asia',
      url: siteUrl,
    },
    datePublished: '2026-06-01',
    dateModified: '2026-06-29',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteUrl}/article/${article.slug}`,
    },
  };
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <main>
        <Navbar />
        <div className="pt-32 pb-20 px-4 text-center bg-background-50">
          <h1 className="text-2xl font-heading font-bold text-foreground-950">Article Not Found</h1>
          <p className="mt-3 text-foreground-600">The article you are looking for does not exist.</p>
          <Link to="/" className="inline-block mt-6 px-6 py-3 text-sm font-semibold text-background-50 bg-primary-500 rounded-lg whitespace-nowrap">
            Back to Home
          </Link>
        </div>
        <Footer />
      </main>
    );
  }

  return (
    <main>
      <JsonLd data={buildArticleSchema(article)} />
      <Navbar />
      <article className="pt-24 pb-14 md:pb-20 px-4 md:px-6 bg-background-50">
        <div className="max-w-3xl mx-auto">
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6 flex-wrap">
            <Link to="/" className="hover:text-foreground-800 transition-colors">Home</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-800">{article.category}</span>
          </nav>

          <header className="mb-8">
            <span className="inline-block px-3 py-1 text-xs font-semibold text-accent-700 bg-accent-100 rounded-full mb-4">
              {article.category}
            </span>
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 leading-tight">
              {article.title}
            </h1>
            <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">
              {article.excerpt}
            </p>
            <div className="flex items-center gap-4 mt-4">
              <div className="flex items-center gap-2">
                <span className="w-8 h-8 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 text-xs font-bold">
                  PB
                </span>
                <div>
                  <p className="text-xs font-semibold text-foreground-800">Pandabuy.asia Team</p>
                  <p className="text-xs text-foreground-500">Updated June 2026 · {article.readTime}</p>
                </div>
              </div>
            </div>
          </header>

          <div className="relative w-full aspect-[16/9] rounded-xl overflow-hidden mb-10 bg-background-200">
            <img
              src={article.image}
              alt={article.title}
              className="w-full h-full object-cover object-top"
              loading="eager"
            />
          </div>

          <div className="prose-custom text-sm md:text-base text-foreground-700 leading-relaxed space-y-6">
            {article.content.map((block, idx) => {
              if (block.type === 'heading') {
                return (
                  <h2 key={idx} className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mt-10 mb-4">
                    {block.text}
                  </h2>
                );
              }
              return (
                <p key={idx}>{block.text}</p>
              );
            })}
          </div>

          <div className="mt-12 p-6 bg-background-100 border border-background-200/70 rounded-2xl">
            <h3 className="text-lg font-heading font-bold text-foreground-950 mb-3">
              Ready to Start Shopping?
            </h3>
            <p className="text-sm text-foreground-600 mb-4">
              Browse our complete Pandabuy Spreadsheet with 50,000+ verified links across 14 categories. Updated daily.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold text-background-50 bg-primary-500 hover:bg-primary-600 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-external-link-line"></i>
                Pandabuy Sheet
              </a>
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold text-foreground-700 bg-background-50 border border-background-200/70 hover:bg-background-200 rounded-lg transition-colors whitespace-nowrap"
              >
                <i className="ri-home-line"></i>
                Back to Home
              </Link>
            </div>
          </div>

          <div className="mt-10 pt-8 border-t border-background-200/70">
            <h4 className="text-sm font-semibold text-foreground-900 mb-3">Related Articles</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {articles
                .filter((a) => a.slug !== article.slug)
                .slice(0, 4)
                .map((a) => (
                  <Link
                    key={a.slug}
                    to={`/article/${a.slug}`}
                    className="block text-sm text-primary-600 hover:text-primary-700 transition-colors leading-snug"
                  >
                    {a.title}
                  </Link>
                ))}
            </div>
            <div className="mt-3">
              <Link to="/" className="text-sm text-foreground-500 hover:text-foreground-700 transition-colors">
                Best Pandabuy Spreadsheet 2026 — Browse All Categories
              </Link>
            </div>
          </div>
        </div>
      </article>
      <Footer />
    </main>
  );
}